import { Box, Button, Typography } from '@mui/material';
import { AnimatePresence, motion, useScroll, useSpring } from 'framer-motion';
import { useEffect, useMemo, useRef } from 'react';
import FeatureHighlights from '../components/FeatureHighlights';
import { Container } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import ModernHero from '../components/ModernHero';
import MobileAppSection from '../components/MobileAppSection';
import ModuleSystemSection from '../components/ModuleSystemSection';
import WidgetFreedomSection from '../components/WidgetFreedomSection';
import HowItWorksSection from '../components/HowItWorksSection';
import OnlineMembershipSection from '../components/OnlineMembershipSection';
import OfficialInfrastructureSection from '../components/OfficialInfrastructureSection';
import SuperInfrastructureSection from '../components/SuperInfrastructureSection';
import WhoIsItForSection from '../components/WhoIsItForSection';
import { EndSection } from '../components/EndSection';



const HomePage = () => {
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 50,
    damping: 30,
    restDelta: 0.001
  });
  const sectionRefs = useRef([]);
  const heroRef = useRef();
  const featureHighlightsRef = useRef();
  const widgetSystemDetailRef = useRef();
  const contractWorkflowRef = useRef();
  const mobileAppSectionRef = useRef();
  const moduleSystemSectionRef = useRef();
  const endSystemSectionRef = useRef();
  const processBridgeSectionRef = useRef();
  const officialInfrastructureRef = useRef();
  const superInfrastructureRef = useRef();
  const onlineMembershipRef = useRef();


  const sections = useMemo(() => [
    { component: ModernHero, ref: heroRef },
    { component: FeatureHighlights, ref: featureHighlightsRef },
    { component: OfficialInfrastructureSection, ref: officialInfrastructureRef },
    { component: SuperInfrastructureSection, ref: superInfrastructureRef },
    { component: ModuleSystemSection, ref: moduleSystemSectionRef },
    { component: WidgetFreedomSection, ref: widgetSystemDetailRef },
    { component: HowItWorksSection, ref: processBridgeSectionRef },
    { component: OnlineMembershipSection, ref: onlineMembershipRef },
    { component: MobileAppSection, ref: mobileAppSectionRef },
    { component: WhoIsItForSection, ref: contractWorkflowRef },
    { component: EndSection, ref: endSystemSectionRef },
  ], []);

  useEffect(() => {
    sectionRefs.current = sections.map(section => section.ref.current);
  }, [sections]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Progress Bar */}
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: '#635BFF',
          transformOrigin: '0%',
          scaleX,
          zIndex: 1000,
        }}
      />
      <AnimatePresence mode="wait">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {sections.map(({ component: Component }, index) => (
            <div key={index} ref={sections[index].ref}>
              <Component />
            </div>
          ))}
        </motion.div>
      </AnimatePresence>

      {/* CTA Bölümü */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f0f9ff 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Dekoratif arka plan - multiple layers */}
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            right: '10%',
            width: 500,
            height: 500,
            background: 'radial-gradient(circle, rgba(8, 145, 178, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            pointerEvents: 'none',
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-10%',
            left: '5%',
            width: 400,
            height: 400,
            background: 'radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(50px)',
            pointerEvents: 'none',
            animation: 'float 6s ease-in-out infinite',
            animationDelay: '1s',
          }}
        />

        {/* Decorative shapes */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: 100,
            height: 100,
            border: '2px solid rgba(8, 145, 178, 0.1)',
            borderRadius: '20px',
            transform: 'rotate(15deg)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '25%',
            right: '15%',
            width: 80,
            height: 80,
            background: 'rgba(16, 185, 129, 0.05)',
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.7)',
                textAlign: 'center',
                p: { xs: 6, md: 10 },
                borderRadius: 5,
                border: '1px solid rgba(8, 145, 178, 0.2)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 20px 60px rgba(8, 145, 178, 0.1), 0 0 0 1px rgba(255,255,255,0.8) inset',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(8, 145, 178, 0.05), transparent)',
                  animation: 'shimmer 3s infinite',
                },
                '@keyframes shimmer': {
                  '0%': { left: '-100%' },
                  '100%': { left: '100%' },
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  color: '#0f172a',
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                }}
              >
                Derneğinizi Yönetimini Dijitale Taşıyın
              </Typography>
              <Typography
                sx={{
                  mb: 5,
                  color: '#475569',
                  fontSize: { xs: '1.05rem', md: '1.2rem' },
                  lineHeight: 1.6,
                  maxWidth: 600,
                  mx: 'auto',
                  fontWeight: 500,
                }}
              >
                Web sitenizi yönetin, üyelerinizle iletişim kurun ve tüm dernek süreçlerini
                tek panelden kontrol edin. 
                <br/>
                SüperDernek ile yönetim artık çok daha kolay.

              </Typography>
              <Box sx={{ display: 'flex', gap: 2.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/iletisim')}
                    sx={{
                      background: 'linear-gradient(135deg, #ff8305 17%, #06C4FE 104%)',
                      color: '#ffffff',
                      fontWeight: 700,
                      px: 6,
                      py: 2.5,
                      fontSize: '1rem',
                      textTransform: 'none',
                      borderRadius: 3,
                      boxShadow: '0 10px 30px rgba(8, 145, 178, 0.3)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 100%)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      },
                      '&:hover': {
                        boxShadow: '0 15px 40px rgba(8, 145, 178, 0.4)',
                        '&::before': {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    Hemen Başlayın
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/ucretlendirme')}
                    sx={{
                      borderColor: 'rgba(8, 145, 178, 0.3)',
                      color: '#0891b2',
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '1rem',
                      px: 6,
                      py: 2.5,
                      borderRadius: 3,
                      borderWidth: 2,
                      backgroundColor: 'rgba(255,255,255,0.5)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(8, 145, 178, 0.08)',
                        borderColor: '#0891b2',
                        borderWidth: 2,
                      },
                    }}
                  >
                    Fiyatları İncele
                  </Button>
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

    </Box>
  );
};

export default HomePage;